import styled, { css } from 'styled-components'

import { ReactComponent as FolderIcon } from '@sketch/icons/folder-closed-17'
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-17'

import {
  CARD_RADIUS,
  CARD_SHADOW,
  GLASS_BLUR,
  NOTCH_HEIGHT,
  NOTCH_MASK_SVG,
  NOTCH_WIDTH,
} from './constants'
import { Image } from '@sketch/components'
import { EditCommentInput } from '@sketch/modules-common'

/**
 * Mask that bakes the notch into the AnnotationCard's shape so the card can clip
 * its scrollable thread with `overflow: hidden`. The top `NOTCH_HEIGHT` strip is
 * masked out (letting the canvas image show through) except for the notch bump.
 * The visible top edge therefore sits at the notch line, so its corners can't be
 * rounded by `border-radius` (which rounds the box edge) — the corner layers
 * round them here instead. All sizes use %/calc so the card stays responsive.
 */
const NOTCH_MASK = [
  // notch bump, top-centre
  `${NOTCH_MASK_SVG} top center / ${NOTCH_WIDTH}px ${NOTCH_HEIGHT}px no-repeat`,
  // body below the rounded top-corner zone
  `linear-gradient(#000 0 0) bottom / 100% calc(100% - ${NOTCH_HEIGHT}px - ${CARD_RADIUS}) no-repeat`,
  // top strip between the two corners
  `linear-gradient(#000 0 0) left ${CARD_RADIUS} top ${NOTCH_HEIGHT}px / calc(100% - 2 * ${CARD_RADIUS}) ${CARD_RADIUS} no-repeat`,
  // top-left rounded corner
  `radial-gradient(${CARD_RADIUS} at 100% 100%, #000 99%, transparent) left 0 top ${NOTCH_HEIGHT}px / ${CARD_RADIUS} ${CARD_RADIUS} no-repeat`,
  // top-right rounded corner
  `radial-gradient(${CARD_RADIUS} at 0% 100%, #000 99%, transparent) right 0 top ${NOTCH_HEIGHT}px / ${CARD_RADIUS} ${CARD_RADIUS} no-repeat`,
].join(', ')

export const Card = styled.section(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    border-radius: ${CARD_RADIUS};
    background: ${theme.colors.background.secondary.A};
    overflow: hidden;
    position: relative;

    box-shadow: ${CARD_SHADOW};
  `
)

export const BreadcrumbItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-size: ${({ theme }) => theme.fontSizes.D};
`

export const BreadcrumbLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const Breadcrumb = styled.div(
  ({ theme }) => css`
    position: absolute;
    inset: 2px 2px auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    color: ${theme.colors.foreground.secondary.B};
    font-size: 12px;
    font-weight: ${theme.fontWeights.medium};

    overflow: hidden;
    background: ${theme.colors.foreground.primary.B};
    backdrop-filter: blur(${GLASS_BLUR});
    border-radius: 18px 18px 8px 8px;
    z-index: 1;
    box-shadow: 0 1px 10px ${theme.colors.background.tertiary.B};

    ${BreadcrumbItem}:last-child {
      color: ${theme.colors.foreground.secondary.A};
    }
  `
)

export const BreadcrumbSeparator = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    opacity: 0.6;
  `
)

export const BreadcrumbFolderIcon = styled(FolderIcon)`
  width: 17px;
  height: 17px;
  flex-shrink: 0;
`

export const BreadcrumbDocumentIcon = styled(DocumentIcon)`
  width: 17px;
  height: 17px;
  flex-shrink: 0;
`

export const CanvasImage = styled(Image)(
  ({ theme }) => css`
    aspect-ratio: 16 / 9;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${theme.colors.background.canvas.A};
    position: relative;
  `
)

export const CanvasImageWrapper = styled.span`
  position: relative;
  overflow: hidden;

  ::before {
    content: '';
    inset: auto 0 0px;
    z-index: 1;
    height: 50px;
    position: absolute;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.06), transparent);
  }
`

export const AnnotationCard = styled.div`
  border-radius: ${CARD_RADIUS};
  background: ${({ theme }) => theme.colors.background.secondary.A};
  position: relative;

  /* Pull up by the original overlap (15px) plus the notch strip, so the notch
   * bump rises above the flat top edge while that edge keeps its old position.
   * The reserved top padding keeps thread content below the notch line. */
  margin-top: calc(-15px - ${NOTCH_HEIGHT}px);
  /* padding-top: ${NOTCH_HEIGHT}px; */

  /* Notch is baked into the card's shape via a mask (see NOTCH_MASK), so the
   * card can clip its scrollable thread with overflow: hidden. */
  overflow: hidden;
  -webkit-mask: ${NOTCH_MASK};
  mask: ${NOTCH_MASK};

  /* Cap the embedded AnnotationCommentsList height so a long thread doesn't
   * stretch the preview indefinitely, while keeping enough room for the
   * comment box to feel usable. The list itself (first child) scrolls; the
   * reply input (last child) stays anchored at the bottom. */
  display: flex;
  flex-direction: column;
  max-height: calc(50vh - 50px);
  z-index: 1;

  > :first-child {
    padding-top: ${NOTCH_HEIGHT}px;
    flex: 1;
    min-height: 0;
  }

  /*
   * Make the edit comment input always visible when opened
   */
  ${EditCommentInput} {
    scroll-margin: 20px;
  }
`
